import { useEffect, useRef, useState } from "react";
import { Box, FormControlLabel, Typography } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import { useLocation, useNavigate } from "react-router-dom";
import pageTextConfig from "../../../../../assets/configs/Add New Gig Page/FinalVerificationPage/textConfig";
import tipsIcon from "../../../../../assets/images/AddNewGigPage/tips_icon_red.png";
import SnackBar, { SnackTypes } from "../../../../UtilityComponents/SnackBar/SnackBar";
import GGLoaderView from "../../../../UtilityComponents/GGLoaderView/GGLoaderView";
import GGModalWindow from "../../../../UtilityComponents/GGModalWindow/GGModalWindow";
import congratsPageConfig from "../../../../../assets/configs/Add New Gig Page/CongratulationsPage/textConfig";
import dashBoardIcon from "../../../../../assets/images/AddNewGigPage/dashboard.svg";
import { SellerPaymentFormData } from "../../../../../pages/AddNewGigPage/PaymentSetupPage/PaymentSetupPage";
import { compressImage } from "../../../../../utils/FileCompressionUtils";
import { ResumableUploadTask } from "../../../../../services/UploadClient/ResumableUploadTask";
import { UploadPurpose } from "../../../../../services/NetworkClient/BackendAPIs/UploadAPIHandler";
import { GigAPIHandler } from "../../../../../services/NetworkClient/BackendAPIs/GigAPIHandler";
import { SellerAPIHandler } from "../../../../../services/NetworkClient/BackendAPIs/SellerAPIhandler";
import { AcceptPaymentMethodEnum } from "../../../../../enums/AcceptPaymentMethodEnum";
import "./AddNewGigUndertakingPage.scss";
import { ErrorLogger } from "../../../../../utils/Logger";
import GGButton from "../../../../CommonComponents/GGButton/GGButton";
import { ArrowBack, Save } from "@mui/icons-material";
import CongratulationsPageForGigReg from "../../../../CommonComponents/CongratulationsPageForGigReg/CongratulationsPageForGigReg";
import { GigFormDataInterface } from "../GigEditingPage";

const AddNewGigUndertakingPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isScrolled, setIsScrolled] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [termsAndconditionsAgreed, setTermsAndconditionsAgreed] = useState([false, false, false, false]);
    const snackBarRef = useRef<{ showSnackbar(): any }>(null);
    const [snackBarParams, setSnackBarParams] = useState({
        message: "",
        state: SnackTypes.error,
    });
    const { gigFormData, selectedCategoryId, selectedSubcategoryId, paymentFormData, hasExistingPayment } = location.state || {};

    useEffect(() => {
        if (!gigFormData || !selectedSubcategoryId) {
            navigate("/freelancer-dashboard/my-gigs/add-new-gig");
            return;
        }
        const handleScroll = () => {
            const scrollContainer = document.querySelector('.dashboard-content-scrollable') as HTMLElement;
            if (scrollContainer) {
                const scrollPosition = scrollContainer.scrollTop;
                setIsScrolled(scrollPosition > 10);
            } else {
                const scrollPosition = window.scrollY || document.documentElement.scrollTop;
                setIsScrolled(scrollPosition > 10);
            }
        };

        const timeoutId = setTimeout(() => {
            const scrollContainer = document.querySelector('.dashboard-content-scrollable') as HTMLElement;
            if (scrollContainer) {
                scrollContainer.addEventListener('scroll', handleScroll);
                handleScroll();
            }
            window.addEventListener('scroll', handleScroll, true);
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            const scrollContainer = document.querySelector('.dashboard-content-scrollable') as HTMLElement;
            if (scrollContainer) {
                scrollContainer.removeEventListener('scroll', handleScroll);
            }
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [gigFormData, selectedSubcategoryId, navigate]);

    const showSnackbar = (message: string, state: SnackTypes) => {
        setSnackBarParams({ message: message, state: state });
        snackBarRef.current?.showSnackbar();
    };

    const updateAgreedTnCList = (agreementId: number) => {
        const updatedList = termsAndconditionsAgreed.map((checkStatus, index) => {
            if (agreementId === index) {
                return !checkStatus;
            }
            return checkStatus;
        });

        setTermsAndconditionsAgreed(updatedList);
    };

    const handleFinalSubmission = () => {
        let allChecked = true;
        termsAndconditionsAgreed.forEach((condition) => {
            if (!condition) {
                allChecked = false;
            }
        });

        if (!allChecked) {
            showSnackbar("Please checkmark all the boxes", SnackTypes.error);
            return;
        }

        performFormSubmission();
    };

    const handleBackPress = () => {
        if (hasExistingPayment) {
            navigate("/freelancer-dashboard/my-gigs/add-new-gig", {
                state: { gigFormData, selectedCategoryId, selectedSubcategoryId },
            });
            return;
        }
        navigate("/freelancer-dashboard/my-gigs/add-new-gig/payments", {
            state: { gigFormData, selectedCategoryId, selectedSubcategoryId, paymentFormData, hasExistingPayment },
        });
    };

    const handleSuccessRedirect = () => {
        setShowSuccessModal(false);
        navigate("/freelancer-dashboard/my-gigs");
    };

    const addPaymentMethod = async (paymentData: SellerPaymentFormData) => {
        let selectedPaymentDetails: string;
        switch (paymentData.selectedPaymentMode) {
            case AcceptPaymentMethodEnum.UPI:
                selectedPaymentDetails = JSON.stringify(paymentData.upiId);
                break;
            case AcceptPaymentMethodEnum.BankAccount:
                selectedPaymentDetails = JSON.stringify(paymentData.bankAccountDetails);
                break;
            default:
                selectedPaymentDetails = "";
        }

        const paymentDetails = [
            {
                paymentMethod: paymentData.selectedPaymentMode,
                paymentDetails: selectedPaymentDetails,
            },
        ];

        await SellerAPIHandler.shared.updateSeller({ paymentDetails });
    };

    const handleFilesUpload = async (files: File[], purpose: UploadPurpose) => {
        return Promise.all(files.map((file) => new ResumableUploadTask(file, { purpose }).start()));
    };

    const performGigCreation = async (formData: GigFormDataInterface, subcategoryId: string, buyerId: string) => {
        const trimmedData = {
            ...formData,
            title: formData.title.trim(),
            description: formData.description.trim(),
            priceTable: formData.priceTable.map((rowData) => ({
                ...rowData,
                description: rowData.description.trim(),
                minPrice: Number(rowData.minPrice),
                maxPrice: Number(rowData.maxPrice),
                duration: Number(rowData.duration),
            })),
        };

        let gigImages: Array<{ assetId?: string; url: string; position: number }> = [];
        let coverImageUrl = "";
        let coverImgAssetId: string | undefined;

        for (let i = 0; i < trimmedData.images.length; i++) {
            const imageObj = trimmedData.images[i];
            if (imageObj.file !== undefined) {
                const compressedFile = await compressImage(imageObj.file, 0.150);
                const asset = (await handleFilesUpload([compressedFile], UploadPurpose.GIG_PORTFOLIO_IMAGE))[0];
                gigImages.push({ assetId: asset.assetId, url: asset.url || "", position: gigImages.length });
            }
            if (imageObj.remoteUrl !== undefined) {
                gigImages.push({ url: imageObj.remoteUrl, position: gigImages.length });
            }
        }

        if (trimmedData.images.length > 0) {
            const firstImage = trimmedData.images[0];
            if (firstImage.file !== undefined) {
                const compressedCoverImage = await compressImage(firstImage.file, 0.030, 0.3);
                const asset = (await handleFilesUpload([compressedCoverImage], UploadPurpose.GIG_COVER_IMAGE))[0];
                coverImageUrl = asset.url || "";
                coverImgAssetId = asset.assetId;
            } else if (firstImage.remoteUrl !== undefined) {
                coverImageUrl = firstImage.remoteUrl;
            }
        }

        const gigData = {
            title: trimmedData.title,
            description: trimmedData.description,
            priceTable: trimmedData.priceTable,
            gigImgs: gigImages,
            coverImg: coverImageUrl,
            coverImgAssetId,
            isListed: trimmedData.isListed,
            subcategory: subcategoryId,
        };

        await GigAPIHandler.shared.createGig(gigData);
    };

    const performFormSubmission = async () => {
        if (!gigFormData || !selectedSubcategoryId) {
            showSnackbar("Please complete previous steps before submitting.", SnackTypes.error);
            return;
        }

        if (loading) {
            return;
        }

        setLoading(true);
        try {
            if (!hasExistingPayment) {
                if (!paymentFormData) {
                    showSnackbar("Please complete payment details before submitting.", SnackTypes.error);
                    return;
                }
                await addPaymentMethod(paymentFormData);
            }

            const seller = await SellerAPIHandler.shared.getSellerByAuthId();
            const buyerId = seller?.buyer;
            if (!buyerId) {
                showSnackbar("Unable to identify buyer. Please try again.", SnackTypes.error);
                return;
            }

            await performGigCreation(gigFormData, selectedSubcategoryId, buyerId);
            showSnackbar("Gig was successfully created.", SnackTypes.success);
            setShowSuccessModal(true);
        } catch (error: any) {
            ErrorLogger.log(error, 'AddNewGigUndertakingPage.handleSubmit');
            const errorMessage =
                typeof error === "string" ? error : error?.message || error?.response?.data?.message || "Failed to create gig. Please try again.";
            showSnackbar(errorMessage, SnackTypes.error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="new-gig-editing-page add-new-gig-step-page">
            {loading && <GGLoaderView />}
            <GGModalWindow
                open={showSuccessModal}
                onCloseHandler={handleSuccessRedirect}
                title="Success"
                childView={
                    <CongratulationsPageForGigReg
                        title={congratsPageConfig.title}
                        description={congratsPageConfig.description}
                        buttonText="See My Gigs"
                        buttonIcon={<img src={dashBoardIcon} alt="" />}
                        onClickHandler={handleSuccessRedirect}
                        needTopSpace
                    />
                }
            />
            <div className={`gig-edit-header ${isScrolled ? "scrolled" : ""}`}>
                <div className="header-left">
                    <Typography variant="h5" className="gig-title-header">
                        Step(3/3): Undertaking
                    </Typography>
                </div>
                <div className="header-right">
                    <div className="vertical-divider"></div>
                    <div className="header-controls">
                        <div className="header-action-buttons">
                            <GGButton
                                variant="outlined"
                                disableClickShrink
                                startIcon={<ArrowBack />}
                                onClick={handleBackPress}
                                className="cancel-btn"
                                disabled={loading || showSuccessModal}
                            >
                                Previous
                            </GGButton>
                            <GGButton
                                variant="contained"
                                color="success"
                                disableClickShrink
                                startIcon={<Save />}
                                onClick={handleFinalSubmission}
                                disabled={loading || showSuccessModal}
                                sx={{ color: "white", ":hover": { color: "none" } }}
                            >
                                Create New Gig
                            </GGButton>
                        </div>
                    </div>
                </div>
                <div className="mobile-header-layout add-new-gig-mobile">
                    <div className="mobile-action-buttons">
                        <GGButton
                            variant="outlined"
                            disableClickShrink
                            startIcon={<ArrowBack />}
                            onClick={handleBackPress}
                            className="cancel-btn"
                            disabled={loading || showSuccessModal}
                            size="small"
                        >
                            Previous
                        </GGButton>
                        <GGButton
                            variant="contained"
                            color="success"
                            disableClickShrink
                            startIcon={<Save />}
                            onClick={handleFinalSubmission}
                            disabled={loading || showSuccessModal}
                            className="save-btn"
                            size="small"
                        >
                            Submit
                        </GGButton>
                    </div>
                </div>
            </div>

            <Box className="new-gig-undertaking-page">
                <Box className="main-container">
                    <Box className="form-container">
                        <Box className="tips-box">
                            <img src={tipsIcon} className="tip-icon" alt="Quick Tip" />
                            <Typography variant="body1" className="tip-text">
                                {pageTextConfig.warning.description}
                            </Typography>
                        </Box>
                        {termsAndconditionsAgreed.map((_isChecked, index) => (
                            <FormControlLabel
                                key={index}
                                control={
                                    <Checkbox
                                        checked={termsAndconditionsAgreed[index]}
                                        onChange={() => updateAgreedTnCList(index)}
                                    />
                                }
                                label={pageTextConfig.termsAndConditions[index]}
                            />
                        ))}
                    </Box>
                </Box>
                <SnackBar
                    ref={snackBarRef}
                    severity={snackBarParams.state}
                    alertDuration={5000}
                    message={snackBarParams.message}
                />
            </Box>
        </div>
    );
};

export default AddNewGigUndertakingPage;